/**
 * Google Calendar Auth Component
 * Manages Google Calendar OAuth authentication for tenant users
 */

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { orchestratorApi, authFetch } from '../api/orchestrator'
import { CalendarAssignment } from './CalendarAssignment';
import './GoogleCalendarAuth.css';

/**
 * BookingTimezoneSettings Component
 * Manages the calendar_timezone setting in agent_settings for appointment booking
 */
const BookingTimezoneSettings: React.FC = () => {
  const [timezone, setTimezone] = useState('Asia/Singapore');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadTimezone();
  }, []);

  const loadTimezone = async () => {
    try {
      const response = await authFetch('/api/agent-settings');
      const data = await response.json();
      if (data.ok && data.settings) {
        const calendarSettings = data.settings.find((s: any) => s.agent_name === 'calendar_webhook_agent');
        if (calendarSettings && calendarSettings.calendar_timezone) {
          setTimezone(calendarSettings.calendar_timezone);
        }
      }
    } catch (error) {
      console.error('Failed to load timezone:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch('/api/agent-settings/calendar_webhook-agent', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(await orchestratorApi.getAuthHeaders()),
        },
        body: JSON.stringify({ calendarTimezone: timezone }),
      });

      const data = await response.json();
      if (data.ok) {
        setMessage({ type: 'success', text: 'Booking timezone updated successfully!' });
        // Reload page to update header display
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update timezone' });
      }
    } catch (error) {
      console.error('Failed to save timezone:', error);
      setMessage({ type: 'error', text: 'Failed to update timezone. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="gca-loading">Loading timezone settings...</p>;
  }

  return (
    <div className="gca-timezone-content">
      {message && (
        <div className={`gca-message gca-message-${message.type}`}>
          {message.text}
        </div>
      )}
      <div className="gca-timezone-controls">
        <label htmlFor="booking-timezone" className="gca-label">Booking Timezone:</label>
        <select
          id="booking-timezone"
          className="gca-select"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          disabled={saving}
        >
          <option value="Asia/Singapore">Asia/Singapore (SGT, UTC+8)</option>
          <option value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur (MYT, UTC+8)</option>
          <option value="Asia/Jakarta">Asia/Jakarta (WIB, UTC+7)</option>
          <option value="Asia/Bangkok">Asia/Bangkok (ICT, UTC+7)</option>
          <option value="Asia/Manila">Asia/Manila (PST, UTC+8)</option>
          <option value="Asia/Hong_Kong">Asia/Hong_Kong (HKT, UTC+8)</option>
          <option value="Asia/Tokyo">Asia/Tokyo (JST, UTC+9)</option>
          <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT, UTC+10/+11)</option>
          <option value="Europe/London">Europe/London (GMT/BST, UTC+0/+1)</option>
          <option value="Europe/Paris">Europe/Paris (CET/CEST, UTC+1/+2)</option>
          <option value="America/New_York">America/New_York (EST/EDT, UTC-5/-4)</option>
          <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT, UTC-8/-7)</option>
          <option value="America/Chicago">America/Chicago (CST/CDT, UTC-6/-5)</option>
          <option value="UTC">UTC (UTC+0)</option>
        </select>
        <button
          onClick={handleSave}
          disabled={saving}
          className="gca-button gca-button-primary"
          style={{ marginLeft: '10px' }}
        >
          {saving ? 'Saving...' : '💾 Save Timezone'}
        </button>
      </div>
      <p className="gca-hint">
        Current booking timezone: <strong>{timezone}</strong>
      </p>
      <p className="gca-help">
        When a customer says "Book me for tomorrow at 4pm", the AI will interpret "4pm" in this timezone
        and create the calendar event accordingly.
      </p>
    </div>
  );
};

interface GoogleCalendarAuth {
  id: number;
  tenant_id: string;
  user_id: string;
  user_email: string;
  google_email: string | null;
  google_picture: string | null;
  is_active: boolean;
  last_verified_at: string | null;
  last_error: string | null;
  created_at: string;
}

interface Entity {
  id: string;
  name: string;
  phone: string | null;
  selected_calendars: string[] | null;
}

interface GoogleCalendar {
  id: string;
  summary: string;
  description: string | null;
  primary: boolean;
  accessRole: string;
  backgroundColor: string;
  foregroundColor: string;
  isPrimary: boolean;
}

interface AuthStatus {
  authenticated: boolean;
  auth: GoogleCalendarAuth | null;
}

interface GoogleCalendarAuthProps {
  selectedEntityId?: string | null;
}

export const GoogleCalendarAuth: React.FC<GoogleCalendarAuthProps> = ({ selectedEntityId: parentSelectedEntityId }) => {
  const { user } = useAuth0();
  const [auths, setAuths] = useState<GoogleCalendarAuth[]>([]);
  const [currentAuth, setCurrentAuth] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // Calendar selection state
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [availableCalendars, setAvailableCalendars] = useState<GoogleCalendar[]>([]);
  const [selectedCalendars, setSelectedCalendars] = useState<Set<string>>(new Set());
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  const [savingCalendars, setSavingCalendars] = useState(false);

  useEffect(() => {
    loadAuths();
    loadCurrentAuth();
    loadEntities();

    // Check URL params for OAuth callback status
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    if (status === 'success') {
      showMessage('success', 'Google Calendar connected successfully!');
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (status === 'error') {
      showMessage('error', 'Failed to connect Google Calendar. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Load calendars when entity is selected
  useEffect(() => {
    if (selectedEntityId) {
      loadAvailableCalendars();
      loadEntitySelectedCalendars();
    } else {
      setAvailableCalendars([]);
      setSelectedCalendars(new Set());
    }
  }, [selectedEntityId]);

  const loadAuths = async () => {
    try {
      const response = await fetch('/api/admin/google-calendar/auth', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setAuths(data.auths);
      }
    } catch (error) {
      console.error('Failed to load Google Calendar auths:', error);
      showMessage('error', 'Failed to load authenticated users');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentAuth = async () => {
    try {
      const userEmail = user?.email;

      if (!userEmail) {
        setCurrentAuth({ authenticated: false, auth: null });
        return;
      }

      const response = await fetch(`/api/admin/google-calendar/auth/me?email=${encodeURIComponent(userEmail)}`, {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setCurrentAuth(data);
      }
    } catch (error) {
      console.error('[GoogleCalendar] Failed to load current auth status:', error);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await fetch('/api/admin/google-calendar/auth/url', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok && data.authUrl) {
        // Redirect to Google OAuth consent screen
        window.location.href = data.authUrl;
      } else {
        showMessage('error', 'Failed to generate authorization URL');
      }
    } catch (error) {
      console.error('Failed to connect Google Calendar:', error);
      showMessage('error', 'Failed to connect Google Calendar');
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect your Google Calendar? The app will no longer be able to access your calendar.')) {
      return;
    }

    setDisconnecting(true);
    try {
      const response = await fetch('/api/admin/google-calendar/auth', {
        method: 'DELETE',
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', 'Google Calendar disconnected successfully');
        setCurrentAuth({ authenticated: false, auth: null });
        await loadAuths();
      } else {
        showMessage('error', data.error || 'Failed to disconnect');
      }
    } catch (error) {
      console.error('Failed to disconnect Google Calendar:', error);
      showMessage('error', 'Failed to disconnect Google Calendar');
    } finally {
      setDisconnecting(false);
    }
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Calendar selection handlers
  const loadEntities = async () => {
    try {
      const response = await fetch('/api/entities', {
        headers: await orchestratorApi.getAuthHeaders()
      });
      const data = await response.json();
      if (data.ok && data.entities) {
        setEntities(data.entities);
        // Auto-select first entity if available
        if (data.entities.length > 0 && !selectedEntityId) {
          setSelectedEntityId(data.entities[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load entities:', error);
    }
  };

  const loadAvailableCalendars = async () => {
    setCalendarsLoading(true);
    try {
      const response = await fetch(`/api/admin/google-calendar/calendars?entity_id=${encodeURIComponent(selectedEntityId)}`, {
        headers: await orchestratorApi.getAuthHeaders()
      });
      const data = await response.json();
      if (data.ok && data.calendars) {
        setAvailableCalendars(data.calendars);
      }
    } catch (error) {
      console.error('Failed to load calendars:', error);
      showMessage('error', 'Failed to load available calendars');
    } finally {
      setCalendarsLoading(false);
    }
  };

  const loadEntitySelectedCalendars = async () => {
    try {
      const response = await fetch(`/api/entities/${encodeURIComponent(selectedEntityId)}/calendars`, {
        headers: await orchestratorApi.getAuthHeaders()
      });
      const data = await response.json();
      if (data.ok && data.selected_calendars) {
        setSelectedCalendars(new Set(data.selected_calendars));
      }
    } catch (error) {
      console.error('Failed to load selected calendars:', error);
    }
  };

  const handleCalendarToggle = (calendarId: string) => {
    const newSelected = new Set(selectedCalendars);
    if (newSelected.has(calendarId)) {
      newSelected.delete(calendarId);
    } else {
      newSelected.add(calendarId);
    }
    setSelectedCalendars(newSelected);
  };

  const handleSaveCalendars = async () => {
    setSavingCalendars(true);
    try {
      const response = await fetch(`/api/entities/${encodeURIComponent(selectedEntityId)}/calendars`, {
        method: 'POST',
        headers: {
          ...(await orchestratorApi.getAuthHeaders()),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ calendar_ids: Array.from(selectedCalendars) })
      });
      const data = await response.json();
      if (data.ok) {
        showMessage('success', 'Selected calendars saved successfully');
      } else {
        showMessage('error', data.error || 'Failed to save calendars');
      }
    } catch (error) {
      console.error('Failed to save calendars:', error);
      showMessage('error', 'Failed to save calendars');
    } finally {
      setSavingCalendars(false);
    }
  };

  return (
    <div className="google-calendar-auth">
      <div className="gca-header">
        <h2>📅 Google Calendar Integration</h2>
        <p className="gca-description">
          Connect your Google Calendar to enable calendar features for your organization.
          Each team member can connect their own calendar.
        </p>
      </div>

      {message && (
        <div className={`gca-message gca-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* All Authenticated Users in Tenant */}
      <div className="gca-card gca-all-users">
        <h3>Connected Calendars in Your Organization</h3>
        <p className="gca-subtext">
          These team members have connected their Google Calendars to the system.
        </p>

        {loading ? (
          <p>Loading...</p>
        ) : auths.length === 0 ? (
          <p className="gca-empty">No calendars connected yet.</p>
        ) : (
          <table className="gca-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Google Account</th>
                <th>Connected Since</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {auths.map((auth) => {
                const isCurrentUser = currentAuth?.auth?.user_email === auth.user_email;

                return (
                  <tr key={auth.id}>
                    <td>
                      <div className="gca-user-cell">
                        {auth.google_picture && (
                          <img
                            src={auth.google_picture}
                            alt={auth.google_email || 'User'}
                            className="gca-table-avatar"
                          />
                        )}
                        <span>{auth.user_email}</span>
                      </div>
                    </td>
                    <td>{auth.google_email || '-'}</td>
                    <td>{new Date(auth.created_at).toLocaleDateString()}</td>
                    <td>
                      {auth.last_error ? (
                        <span className="gca-badge gca-badge-error" title={auth.last_error}>
                          Invalid ❌
                        </span>
                      ) : (
                        <span className="gca-badge gca-badge-active">Active ✅</span>
                      )}
                    </td>
                    <td>
                      {isCurrentUser ? (
                        <>
                          <button
                            onClick={handleDisconnect}
                            disabled={disconnecting}
                            className="gca-button gca-button-danger gca-button-small"
                          >
                            {disconnecting ? 'Logging out...' : 'Logout'}
                          </button>
                          {auth.last_error && (
                            <div className="gca-error-hint" style={{ marginTop: '4px', fontSize: '0.75rem', color: '#ef4444' }}>
                              Please reconnect
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="gca-text-muted">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Connect Button */}
        {currentAuth !== null && !currentAuth.authenticated && (
          <div className="gca-connect-section">
            <button
              onClick={handleConnect}
              className="gca-button gca-button-primary"
            >
              Connect Google Calendar
            </button>
          </div>
        )}
      </div>

      {/* Entity-Calendar Assignments Section */}
      <CalendarAssignment initialEntityId={parentSelectedEntityId} />

      {/* Calendar Selection Section */}
      {currentAuth?.authenticated && (
        <div className="gca-card gca-calendar-selection">
          <h3>🗓️ Select Calendars for Appointment Booking</h3>
          <p className="gca-subtext">
            Choose which Google Calendars to use when booking appointments for each entity.
            The first selected calendar will be used as the default for booking.
          </p>

          {/* Entity Selector */}
          <div className="gca-entity-selector">
            <label htmlFor="entity-select" className="gca-label">Select Entity:</label>
            <select
              id="entity-select"
              className="gca-select"
              value={selectedEntityId}
              onChange={(e) => setSelectedEntityId(e.target.value)}
              disabled={calendarsLoading}
            >
              <option value="">-- Select Entity --</option>
              {entities.map((entity) => (
                <option key={entity.id} value={entity.id}>
                  {entity.name} {entity.phone ? `(${entity.phone})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Calendars List */}
          {selectedEntityId && (
            <>
              {calendarsLoading ? (
                <p>Loading calendars...</p>
              ) : availableCalendars.length === 0 ? (
                <p className="gca-empty">
                  No calendars found. Please connect your Google Calendar first.
                </p>
              ) : (
                <>
                  <div className="gca-calendars-list">
                    {availableCalendars.map((calendar) => {
                      const isSelected = selectedCalendars.has(calendar.id);
                      return (
                        <div
                          key={calendar.id}
                          className={`gca-calendar-item ${isSelected ? 'gca-calendar-item-selected' : ''}`}
                          onClick={() => handleCalendarToggle(calendar.id)}
                        >
                          <div className="gca-calendar-checkbox">
                            <input
                              type="checkbox"
                              id={`calendar-${calendar.id}`}
                              checked={isSelected}
                              onChange={() => handleCalendarToggle(calendar.id)}
                            />
                            <label htmlFor={`calendar-${calendar.id}`}>
                              <div className="gca-calendar-info">
                                <div className="gca-calendar-name">
                                  {calendar.summary}
                                  {calendar.isPrimary && (
                                    <span className="gca-badge gca-badge-primary">Primary</span>
                                  )}
                                </div>
                                {calendar.description && (
                                  <div className="gca-calendar-description">{calendar.description}</div>
                                )}
                                <div className="gca-calendar-id">{calendar.id}</div>
                              </div>
                            </label>
                          </div>
                          <div
                            className="gca-calendar-color"
                            style={{ backgroundColor: calendar.backgroundColor }}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Save Button */}
                  <div className="gca-save-section">
                    <button
                      onClick={handleSaveCalendars}
                      disabled={savingCalendars}
                      className="gca-button gca-button-primary"
                    >
                      {savingCalendars ? 'Saving...' : `Save Selected Calendars (${selectedCalendars.size})`}
                    </button>
                    {selectedCalendars.size > 0 && (
                      <span className="gca-hint">
                        First calendar: {availableCalendars.find(c => c.id === Array.from(selectedCalendars)[0])?.summary || 'Unknown'}
                      </span>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Booking Timezone Settings */}
      <div className="gca-card gca-timezone-settings">
        <h3>🌏 Booking Timezone Settings</h3>
        <p className="gca-subtext">
          Configure the timezone for AI appointment booking. When customers book appointments via chat,
          all times will be interpreted in this timezone.
        </p>
        <BookingTimezoneSettings />
      </div>

      {/* Info Section */}
      <div className="gca-card gca-info">
        <h3>ℹ️ How It Works</h3>
        <ol className="gca-steps">
          <li>Each team member connects their own Google Calendar account</li>
          <li>The app stores OAuth tokens securely (tenant-isolated)</li>
          <li>Tokens are automatically refreshed when needed</li>
          <li>The app can access all connected calendars for scheduling</li>
        </ol>
        <div className="gca-note">
          <strong>Privacy Note:</strong> Your calendar data is isolated within your organization's tenant.
          Other tenants cannot access your calendar information.
        </div>
      </div>
    </div>
  );
};

export default GoogleCalendarAuth;
